// src/pages/Dashboard.tsx
import React, { ReactElement } from 'react';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import { 
  Search as AnalyzeIcon, 
  Edit as RewriteIcon, 
  Add as GenerateIcon 
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/common/LoadingSpinner';
import WaveBackground from '../components/common/WaveBackground';
import { useNavigate } from 'react-router-dom';

interface DashboardCardProps {
  title: string;
  description: string;
  icon: ReactElement;
  buttonText: string;
  onClick: () => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, description, icon, buttonText, onClick }) => (
  <Paper 
    sx={{ 
      p: 4,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
      backgroundColor: 'rgba(36, 59, 83, 0.8)',
      '&:hover': {
        transform: 'translateY(-4px)',
        backgroundColor: 'rgba(45, 74, 109, 0.9)'
      }
    }}
  >
    <Box 
      sx={{ 
        backgroundColor: '#00A4E0',
        borderRadius: '50%',
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}
    >
      {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
    </Box>
    <Typography variant="h6" sx={{ mb: 2, color: '#FFFFFF' }}>
      {title}
    </Typography>
    <Typography variant="body1" sx={{ mb: 3, flexGrow: 1, color: '#E2E8F0' }}>
      {description}
    </Typography>
    <Button 
      variant="contained" 
      fullWidth
      onClick={onClick}
      sx={{
        backgroundColor: '#B4E33D',
        color: '#1B2B3A',
        '&:hover': {
          backgroundColor: '#9BC535'
        }
      }}
    >
      {buttonText}
    </Button>
  </Paper>
);

const Dashboard = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box 
      sx={{ 
        position: 'relative',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #1B2B3A 0%, #243B53 100%)',
        overflow: 'hidden'
      }}
    >
      <WaveBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1, px: 4, py: 6, flexGrow: 1 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 1,
            textAlign: 'center',
            color: '#FFFFFF'
          }}
        >
          Template Management
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 6,
            textAlign: 'center',
            maxWidth: 600,
            mx: 'auto',
            color: '#E2E8F0'
          }}
        >
          Create, analyze, and manage phishing templates for security awareness training.
        </Typography>

        <Grid container spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Analyze Template"
              description="Analyze email templates for potential phishing indicators."
              icon={<AnalyzeIcon />}
              buttonText="Analyze Template"
              onClick={() => navigate('/analyze')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Rewrite Template"
              description="Modify existing templates to adjust their phishing characteristics."
              icon={<RewriteIcon />}
              buttonText="Rewrite Template"
              onClick={() => navigate('/rewrite')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DashboardCard
              title="Generate Template"
              description="Create new phishing templates based on specified parameters."
              icon={<GenerateIcon />}
              buttonText="Generate Template"
              onClick={() => navigate('/generate')}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
